import React, { memo, useContext } from 'react'

import { Button, Icon, ArrowLeft, classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, number, oneOfType, shape, string } from 'prop-types'

import { iconSize } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import Composer from 'Pods/ThreeDStudio/Shared/Composer/Composer'
import DownloadButton from 'Pods/ThreeDStudio/Shared/DownloadButton/DownloadButton'
import { VERSION_KIND } from 'Pods/ThreeDStudio/Shared/Utils/imageRun'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import { EMPTY_ARRAY, NOOP } from 'Utils/stableDefaults'

import MaskCanvas from './Components/Canvas/MaskCanvas'
import HistoryControl from './Components/HistoryControl/HistoryControl'
import PromptViewer from './Components/PromptViewer/PromptViewer'
import ShareButton from './Components/ShareButton/ShareButton'
import Toolbar from './Components/Toolbar/Toolbar'
import useEditCanvas from './useEditCanvas'

const backIcon = <Icon svgComponent={ArrowLeft} size={iconSize} notSpaced color="darker" />

// Holds the live editing session. Remounted (via the key its host renders it with)
// whenever a new image is loaded, so its seeded state resets cleanly; stays mounted
// across tab switches so the work is preserved.
//
// The image, the session and the callbacks are props rather than store reads, so the
// assistant panel's Edit tab and the full-width editor each drive their own instance.
// renderHeaderActions receives the version currently on the canvas, which is the only
// piece of internal state a host's header needs.
function EditCanvas({
  image,
  sessionId = undefined,
  isActive = true,
  getSessionId = null,
  onSessionCreated = NOOP,
  onStartNew = NOOP,
  onVersionChange = NOOP,
  isStartNewHidden = false,
  renderHeaderActions = null,
  customClass = '',
  composerClass = '',
}) {
  const t = useTranslation()
  const { twoDImagesIdeationFlowEnabled, unfinishedTwoDFeaturesEnabled } = useContext(ThreeDStudioPageContext)
  const hasImageTools = twoDImagesIdeationFlowEnabled && unfinishedTwoDFeaturesEnabled
  const placeholderKey = hasImageTools ? 'placeholderWithImageTools' : 'placeholder'
  const {
    canvas,
    currentImage,
    versions,
    versionCount,
    prompt,
    attachments,
    isHistoryOpen,
    isSending,
    isSendDisabled,
    isUploadDisabled,
    fileInputRef,
    handlePromptChange,
    handleUploadClick,
    handleRemoveAttachment,
    handleFilesChange,
    handleSend,
    handleToggleHistory,
    handleCloseHistory,
    handleSelectVersion,
    handleStartNew,
  } = useEditCanvas({
    image, sessionId, isActive, getSessionId, onSessionCreated, onStartNew, onVersionChange,
  })

  return (
    <div className={classnames('image-assistant-edit-tab__edit', customClass)}>
      <div className="image-assistant-edit-tab__edit-scroll">
        <div
          className={classnames(
            'image-assistant-edit-tab__header',
            isStartNewHidden && 'image-assistant-edit-tab__header--actions-only',
          )}
        >
          {!isStartNewHidden && (
            <Button
              type="secondary"
              size="small"
              variant="with-icon"
              actionType="button"
              customClass="image-assistant-edit-tab__back"
              icon={backIcon}
              onClick={handleStartNew}
            >
              {t('imageAssistantPanel.editTab.back')}
            </Button>
          )}
          {renderHeaderActions
            ? renderHeaderActions(currentImage)
            : currentImage && (
              <div className="image-assistant-edit-tab__header-actions">
                {currentImage.slug && (
                  <ShareButton slug={currentImage.slug} customClass="image-assistant-edit-tab__share" />
                )}
                <DownloadButton
                  result={currentImage}
                  customClass="image-assistant-edit-tab__download"
                  isIconOnly
                />
              </div>
            )}
        </div>
        <div className="image-assistant-edit-tab__canvas-card">
          <MaskCanvas
            image={currentImage}
            canvasRef={canvas.canvasRef}
            containerRef={canvas.containerRef}
            brushSize={canvas.brushSize}
            isErasing={canvas.isErasing}
            isSending={isSending}
            overlay={(
              <>
                <HistoryControl
                  versions={versions}
                  versionCount={versionCount}
                  currentId={currentImage?.id}
                  isOpen={isHistoryOpen}
                  isSending={isSending}
                  onToggle={handleToggleHistory}
                  onSelectVersion={handleSelectVersion}
                  onClose={handleCloseHistory}
                />
                {currentImage?.prompt && currentImage.kind === VERSION_KIND.PROMPT
                  ? <PromptViewer key={currentImage.id} prompt={currentImage.prompt} />
                  : null}
              </>
            )}
            onPointerDown={canvas.handlePointerDown}
            onPointerMove={canvas.handlePointerMove}
            onPointerUp={canvas.handlePointerUp}
          />
        </div>
      </div>
      <Composer
        prompt={prompt}
        attachments={attachments}
        tags={EMPTY_ARRAY}
        fileInputRef={fileInputRef}
        customClass={composerClass}
        sendLabel={t('imageAssistantPanel.actions.editImage')}
        placeholder={t(`imageAssistantPanel.editTab.composer.${placeholderKey}`)}
        tools={(
          <Toolbar
            canUndo={canvas.canUndo}
            canRedo={canvas.canRedo}
            isErasing={canvas.isErasing}
            brushSize={canvas.brushSize}
            isSending={isSending}
            onUndo={canvas.undo}
            onRedo={canvas.redo}
            onErasingChange={canvas.setErasing}
            onBrushSizeChange={canvas.setBrushSize}
          />
        )}
        hasMobileToolRow
        isSendDisabled={isSendDisabled}
        isUploadDisabled={isUploadDisabled}
        isUploadHidden
        onPromptChange={handlePromptChange}
        onTagClick={NOOP}
        onUploadClick={handleUploadClick}
        onRemoveAttachment={handleRemoveAttachment}
        onFilesChange={handleFilesChange}
        onSend={handleSend}
      />
    </div>
  )
}

EditCanvas.propTypes = {
  image: shape({
    id: oneOfType([string, number]).isRequired,
    url: string.isRequired,
    alt: string,
    slug: string,
  }).isRequired,
  sessionId: oneOfType([string, number]),
  isActive: bool,
  getSessionId: func,
  onSessionCreated: func,
  onStartNew: func,
  onVersionChange: func,
  isStartNewHidden: bool,
  renderHeaderActions: func,
  customClass: string,
  composerClass: string,
}

export default memo(EditCanvas)
